import Idea from "@/types/Idea"
import IdeaProperty from "@/types/IdeaProperty"
import Instance from "@/types/Instance"
import Domain, {domainOfProperty} from "@/types/Domain"
import {EnhancedWorkflow} from "@/types/Workflow"
import {LaneOption, ValueForm} from "@/types/ColoredOption"
import {instanceColor, NO_VALUE_COLOR, personColor} from "@/utils/colors"
import Plain from "@/types/Plain"
import {asArray} from "@/utils/arrayUtils"
import {
    isChoiceField, personName, propertyValue, relatedId as idOfRelated, relatedValues, ValueLookup
} from "@/utils/propertyDisplay"
import {WorkspacePerson} from "@/types/Person"
import {effectiveCardinality, NameResolver, travelsAsList} from "@/utils/propertyDefinition"

/**
 * As dimensões pelas quais o Kanban agrupa: a situação do fluxo, um atributo de seleção, um
 * relacionamento.
 *
 * As três respondem às mesmas perguntas — quais valores existem, em qual deles este item cai,
 * como se chama e de que cor é — e até aqui cada tela respondia a cada uma do seu jeito. Uma
 * `Dimension` é essa resposta, escrita uma vez.
 *
 * Puro de propósito: sem Vue, sem storage, sem requisição. É o que permite ao tabuleiro e ao
 * sandbox de desenvolvimento agruparem pelo mesmo código, em vez de o sandbox imitá-lo — que é
 * o mesmo motivo pelo qual a faixa foi extraída um dia.
 */

/**
 * Como os valores DESTA dimensão se desenham — ver `ValueForm`, onde o vocabulário está escrito.
 *
 * A natureza do valor sai direto do tipo da dimensão: relacionamento aponta item de coleção,
 * situação é situação, e seleção é opção de domínio. A tradução vive aqui, perto do `kind`, para
 * as telas que mostram valor de dimensão — o menu de raias, o modal de troca rápida — não
 * responderem cada uma à sua maneira.
 */
export function formOf(dimension: Dimension): ValueForm {
    return dimension.kind === 'rel' ? 'accent'
        : dimension.kind === 'state' ? 'dot'
            : dimension.kind === 'user' ? 'person' : 'chip'
}

/** A chave da dimensão de situação. É também a chave do valor no documento da instância. */
export const STATE_KEY = '_state'

/**
 * O id do grupo "Não definido": onde cai o item cuja propriedade está vazia.
 *
 * Um id de VERDADE, e não `undefined`, e é isso que faz o grupo custar quase nada: ele entra na
 * lista de valores, no menu de raias, na preferência guardada, na coordenada do bloco e no
 * `:key` da faixa como qualquer outro valor, sem um caso especial em cada um desses lugares.
 * Só a gravação o traduz, para `null`, que é como o servidor entende "apague este campo".
 *
 * Zero nunca colide: id de situação e id de opção vêm da `Sequence` do servidor, que grava 1 no
 * `create()` e só cresce.
 */
export const NO_VALUE_ID = 0

export type DimensionKind = 'state' | 'choice' | 'rel' | 'user'

export type Dimension = {
    /**
     * `_state` ou o alias da propriedade.
     *
     * É a chave da dimensão E a chave que entra no `changes` de uma gravação — a situação
     * mora em `_state` no documento, e um atributo mora no próprio alias. É o que faz o
     * arrasto funcionar sem um caso especial por tipo.
     */
    key: string
    name: string
    kind: DimensionKind
    /**
     * A propriedade, quando a dimensão é uma. A situação não tem — ela mora em `_state`, fora
     * de `idea.properties`, e é justamente essa assimetria que faz a dimensão existir.
     */
    property?: IdeaProperty
    /** Os grupos, na ordem em que aparecem. */
    values: LaneOption[]
    /**
     * Quais grupos ficam à vista para quem nunca escolheu. Ausente é "todos".
     *
     * Só a dimensão de relacionamento usa: os valores dela incluem TODOS os itens da coleção
     * apontada, e abrir um quadro com uma raia por candidato — a maioria vazia — seria pior do
     * que o problema que os candidatos vieram resolver. O padrão são os que têm item; o resto
     * fica desmarcado no menu, à mão de quem quiser.
     *
     * Não é um sumiço: o modal de mover lê `values`, e não os visíveis, então um card continua
     * podendo ir para um valor que ainda não tem raia — e a raia aparece depois, porque aí o
     * valor passa a ter item. Era isso que não existia antes dos candidatos.
     *
     * Situação e seleção não usam: as situações de um fluxo e as opções de um domínio são
     * poucas, fixas e desenhadas pelo usuário — esconder as vazias esconderia o desenho dele.
     */
    defaultValues?: number[]
    /**
     * Em que grupos este item cai. Nunca vazio: sem valor, `[NO_VALUE_ID]`.
     *
     * **Lista, e não um valor só**, porque um relacionamento múltiplo põe o mesmo item em várias
     * raias — é o que mostra que a demanda pertence a dois projetos. As dimensões de um valor só
     * devolvem uma lista de um, e nada no agrupamento precisa saber a diferença.
     */
    valuesOf(instance: Instance): number[]
    /**
     * O item pode ter VÁRIOS valores nesta dimensão? É o que muda o significado de arrastar —
     * com vários, soltar numa raia pode ser mover ou acrescentar.
     *
     * No espelho isto sai do outro lado: é múltiplo quando o campo de lá NÃO é exclusivo, e aí
     * o mesmo item de cá cabe em vários de lá.
     */
    multiple?: boolean
    /**
     * O campo viaja como LISTA, ainda que comporte um valor só.
     *
     * Pergunta diferente da de cima, e por isso um campo próprio: um espelho de campo exclusivo
     * tem no máximo um valor — não é múltiplo —, mas continua chegando e sendo gravado como
     * array, porque o valor dele mora nos documentos do outro lado. Confundir as duas fazia a
     * gravação mandar um id solto onde o servidor espera a lista.
     */
    list?: boolean
    /**
     * Só em `rel`: o objeto do `_rels` daquele valor.
     *
     * Serve a duas coisas: desenhar o chip do rótulo com o identificador resolvido, e
     * consertar o `_rels` depois de um arrasto — sem isso o card continuaria mostrando o
     * valor antigo, porque o `propertyValue` lê `_rels` antes do campo cru.
     */
    relatedOf?(id: number): any
}

/**
 * Uma propriedade pode ser dimensão?
 *
 * **Só valores discretos.** Relacionamento e atributo de seleção servem; número, texto e data
 * são faixas, não grupos.
 *
 * **A pergunta é quantos itens cabem no MESMO grupo**, e não quantos valores o campo guarda.
 * Cabendo um só, cada raia teria um card, e um quadro assim não compara nada. `multiple` nunca
 * foi a pergunta: um campo múltiplo agrupa muito bem, pondo o mesmo card em cada raia a que ele
 * pertence.
 *
 * No relacionamento isso se lê do OUTRO lado — cabem vários itens desta coleção lá? — e o
 * `effectiveCardinality` faz essa virada sozinho.
 *
 * É a mesma separação que o `isRangeType` da listagem faz, e pelo mesmo motivo: texto, número
 * e data são faixas, não grupos.
 *
 * **Relacionamento que é CHAVE fica de fora, dos dois lados.** Chave desta coleção: o
 * identificador é o que vira o título do card, então a raia diria exatamente o que cada card
 * dela já diz na primeira linha — "Matemática" no nome da raia e "Matemática · Prova 1" em todo
 * card dentro dela. Chave da coleção de destino: o mesmo, ao contrário — ver o `keyPointsBack`.
 * Nos dois casos quem agrupa e quem é agrupado dizem a mesma coisa, e a raia deixa de comparar
 * qualquer coisa.
 *
 * Vale só para relacionamento. Um atributo de seleção que é chave continua servindo: o card
 * também repete o valor no título, mas as opções de um domínio são poucas e fixas, e o quadro
 * agrupado por elas ainda compara — que é justamente o que se perde no relacionamento, onde
 * cada valor costuma ter um item só.
 */
export function isDimensionProperty(property: IdeaProperty, ideaAlias: string, ideas: Idea[]): boolean {
    //Só valores discretos: os campos de escolha — relacionamento, seleção, usuário. Número, texto
    //e data são faixas.
    if (!isChoiceField(property)) return false
    //Calculada fica fora: arrastar um card para outra raia GRAVA o valor da dimensão, e numa
    //calculada quem decide o valor é a fórmula — o servidor agora recusa essa escrita. Agrupar só
    //para ver, sem arrastar, é a fase da dimensão calculada somente de leitura.
    if (property.formula) return false
    //Relacionamento que é chave fica fora dos dois lados: aqui, quando ele é parte da chave
    //desta coleção; ali, quando é parte da chave da coleção de destino. Ver abaixo.
    //
    //Só no relacionamento: um atributo de seleção que é chave continua servindo, pelo motivo
    //que o comentário acima registra.
    if (property.relationship &&
        (property.identifier || keyPointsBack(property, ideaAlias, ideas))) return false
    //A pergunta é quantos itens DESTA coleção caem no mesmo grupo, e não quantos valores este
    //campo guarda. Exclusivo quer dizer um só — uma raia por card, que é uma lista com molduras.
    //
    //UMA fórmula para as duas espécies. A seleção decidia por `!multiple && type === 'choice'`,
    //que era a pergunta errada pelo mesmo motivo que ela era errada no relacionamento: um campo
    //múltiplo agrupa muito bem, pondo o card em cada raia a que ele pertence. O preço de
    //unificar é que a seleção passa a respeitar `unique`, que ela ignorava — e isso é o que se
    //quer, porque ali também significa uma raia por card.
    //
    //O `effectiveCardinality` já responde pelos dois: sem relacionamento devolve o `multiple` e
    //o `unique` declarados; no espelho, deriva a exclusividade do `multiple` do outro lado.
    return !effectiveCardinality(property, resolverFor(ideas)).unique
}

/**
 * O resolvedor que o `effectiveCardinality` pede, montado das coleções que já chegam aqui.
 *
 * Existe porque este módulo recebe `ideas` — é o que o `keyPointsBack` precisa, já que ele lê a
 * CHAVE da coleção de destino, e não uma propriedade dela — enquanto aquele utilitário fala a
 * língua do `NameResolver`. Só o `property` é usado; os outros dois estão aqui para satisfazer o
 * tipo, e devolvem o que um resolvedor sem ambiente pode devolver.
 */
function resolverFor(ideas: Idea[]): NameResolver {
    return {
        idea: alias => alias,
        property: (ideaAlias, propertyAlias) =>
            ideas.find(idea => idea.alias === ideaAlias)?.properties
                .find(property => property.alias === propertyAlias),
        domain: () => undefined
    }
}

/**
 * A chave do outro lado aponta de volta para cá?
 *
 * Se aponta, agrupar por este relacionamento não distingue nada: o nome de cada raia sai do
 * identificador do item apontado, e esse identificador CONTÉM o item que está na raia. Lê-se
 * "Matemática · Prova final" numa raia de disciplinas — a disciplina servindo de rótulo para
 * ela mesma.
 *
 * E não é só feio: um item cuja chave inclui o dono pertence a um dono só, então cada item do
 * quadro ganharia a própria raia, com um card dentro. Isso é uma lista, não um Kanban.
 *
 * Basta a chave de lá ter UM relacionamento para cá, sem exigir que seja o espelho declarado
 * deste: o que produz o rótulo repetido é a chave conter a volta, e não a forma como os dois
 * lados foram declarados — que nem sempre é preenchida.
 */
function keyPointsBack(property: IdeaProperty, ideaAlias: string, ideas: Idea[]): boolean {
    const target = ideas.find(idea => idea.alias === property.relationship!.idea)
    return (target?.properties || [])
        .some(other => other.identifier && other.relationship?.idea === ideaAlias)
}

/**
 * O Kanban é a visão NATURAL desta coleção — a que se abre sem ninguém ter pedido?
 *
 * **Fluxo é a coleção dizendo que o trabalho dela anda por etapas**, e um quadro é o desenho
 * disso. Um atributo de seleção é mais um campo: serve para agrupar quando alguém QUER agrupar,
 * mas não anuncia que a coleção se lê em colunas. Por isso o padrão pergunta pelo fluxo,
 * enquanto o portão abaixo pergunta por qualquer dimensão.
 *
 * Esta é a pergunta ESTREITA, e o `hasDimension` a larga — e é de propósito que ele comece por
 * ela: assim o código mostra que todo padrão é também disponível, e não só o contrário. A
 * `CollectionEntry` depende disso para validar a visão lembrada sem um caso especial.
 */
export function kanbanByDefault(idea: Idea): boolean {
    return !!idea.workflow
}

/**
 * A coleção tem por onde agrupar?
 *
 * É o portão do Kanban: o que EXISTE, e não o que se abre sozinho — ver o `kanbanByDefault`.
 * Era "tem fluxo", de quando o quadro só sabia agrupar por situação — uma coleção com "Tipo
 * (Bug/Melhoria)" e sem fluxo nenhum era empurrada de volta para a listagem, e é justamente ela
 * quem mais ganha com o quadro.
 *
 * Recebe as coleções do ambiente porque a elegibilidade de um relacionamento depende da chave
 * da coleção de DESTINO — ver o `keyPointsBack`.
 *
 * `fixed` são as chaves que o recorte já prende (ver `fixedByFilter`). Sem elas o portão
 * abriria para um quadro que não tem por onde agrupar: uma coleção cuja única dimensão é o
 * campo que aponta para o dono oferece Kanban vista inteira, e não vista de um dono só.
 */
export function hasDimension(idea: Idea, ideas: Idea[], fixed: string[] = []): boolean {
    return kanbanByDefault(idea)
        || (idea.properties || []).some(property =>
            !fixed.includes(property.alias) && isDimensionProperty(property, idea.alias, ideas))
}

/** A situação do fluxo como dimensão. A ordem dos valores é a do fluxo. */
export function stateDimension(workflow: EnhancedWorkflow, name: string): Dimension {
    return {
        key: STATE_KEY,
        name,
        kind: 'state',
        //coloredStates() e não states: a cor pode ser automática, e é o fluxo que a resolve.
        //Já vem na forma de LaneOption, com o `final` que liga o prazo dos terminados.
        values: workflow.coloredStates(),
        //currentStateOf e não `_state` cru: item sem situação está no começo do fluxo, e essa
        //regra mora no fluxo.
        valuesOf: (instance: Instance) => [workflow.currentStateOf(instance).id]
    }
}

/**
 * Um atributo de seleção como dimensão. A ordem dos valores é a do domínio, e o grupo
 * "Não definido" vai no fim — a ordem das opções foi desenhada pelo usuário, e mexer nela
 * seria desfazer uma decisão dele.
 */
export function choiceDimension(
    property: IdeaProperty, domains: Domain[], instances: Instance[], noValueName: string
): Dimension {
    const domain = domainOfProperty(property, domains)
    //O campo CRU: a opção escolhida mora no próprio documento, e não no `_rels`.
    const valuesOf = (instance: Instance) => groupsOf(instance[property.alias])
    return {
        key: property.alias,
        name: property.name,
        kind: 'choice',
        property,
        values: [...(domain ? domain.options : []),
            ...noValueGroup(property, hasMissing(instances, valuesOf), noValueName)],
        valuesOf,
        ...shapeOf(property)
    }
}

/**
 * Um atributo de usuário como dimensão: uma raia por pessoa — as que se podem escolher, e as que
 * saíram do ambiente mas ainda estão em algum item do quadro, que senão sumiriam dele.
 *
 * Na ordem do nome, que é como as pessoas se procuram — e não por peso, como no relacionamento,
 * que não tem ordem nenhuma. A cor da raia é a do avatar da pessoa (`personColor`): a raia e a cara
 * dizem a mesma cor.
 *
 * À vista por padrão, só as que têm item, como no relacionamento: um ambiente de vinte pessoas não
 * abre com vinte raias, quase todas vazias. As outras ficam no menu, e no modal de mover.
 */
export function userDimension(
    property: IdeaProperty, people: WorkspacePerson[], instances: Instance[], noValueName: string
): Dimension {
    //O campo CRU: o id da conta mora no próprio documento, como a opção da seleção
    const valuesOf = (instance: Instance) => groupsOf(instance[property.alias])
    const present = new Set(instances.flatMap(valuesOf))
    const values: LaneOption[] = [
        ...people.filter(person => person.assignable || present.has(person.user))
            .map(person => ({id: person.user, name: personName(person), color: personColor(person.user), person})),
        ...noValueGroup(property, present.has(NO_VALUE_ID), noValueName)
    ]
    return {
        key: property.alias,
        name: property.name,
        kind: 'user',
        property,
        values,
        //O grupo vazio entra sempre, pela razão do relacionamento: é por ele que se esvazia o campo
        defaultValues: values.filter(value => value.id === NO_VALUE_ID || present.has(value.id)).map(value => value.id),
        valuesOf,
        ...shapeOf(property)
    }
}

/**
 * Um relacionamento como dimensão, com os valores PRESENTES entre os itens do quadro.
 *
 * Não são todos os candidatos: a coleção de destino pode ter centenas de itens, e o produto
 * cartesiano de blocos os multiplicaria. Presentes é o que o quadro consegue mostrar, e é o
 * que o `uniqueRelValues` da listagem já calculava do mesmo jeito.
 *
 * **A cor vem do ID de cada item**, pelo `instanceColor`: é a mesma cor que ele tem no chip, na
 * bolinha do campo de escolha e no ornamento da ficha dele. Antes vinha da posição no quadro, o
 * que fazia a raia trocar de cor quando a contagem mudava e discordar do resto do app.
 */
export function relationshipDimension(
    property: IdeaProperty, instances: Instance[], noValueName: string, candidates: any[] = [],
    ideas: Idea[] = []
): Dimension {
    //Os itens apontados, sem repetição. O mapa serve a duas coisas: nomear cada valor e devolver
    //o objeto resolvido, que o chip do rótulo e o conserto do `_rels` precisam.
    //
    //**Os candidatos primeiro, os presentes por cima.** Sem os candidatos, um valor que ainda
    //não tem item nenhum não vira raia — e sem raia não há para onde arrastar, nem o que
    //oferecer no modal de mover, que lê esta mesma lista. O quadro da coleção inteira disfarçava
    //isso, porque com todos os itens à vista quase todo candidato tem pelo menos um; o de um
    //relacionamento, recortado, mostrava o buraco.
    //
    //A sobrescrita importa: o objeto do `_rels` é o mesmo item com mais coisa dentro, e é ele
    //que o rótulo do bloco espera para abrir a ficha.
    const related = new Map<number, any>()
    candidates.forEach(candidate => {
        const id = relatedId(candidate)
        if (id !== undefined) related.set(id, candidate)
    })
    relatedValues(instances, property.alias)
        .forEach(value => related.set(relatedId(value), value))
    //TODOS os valores, e não o primeiro: era o primeiro porque a dimensão só aceitava
    //relacionamento de um valor só, e ali os dois eram a mesma coisa. Vazio vira [NO_VALUE_ID],
    //que é o grupo "sem valor" e precisa existir como id de verdade — ver o NO_VALUE_ID.
    //propertyValue e não o campo cru: o valor de um relacionamento mora no `_rels`, com o
    //identificador resolvido; o campo guarda só o id.
    const valuesOf = (instance: Instance) => groupsOf(propertyValue(instance, property.alias))

    //Uma contagem só, pelo mesmo valuesOf que agrupa. Um item com dois valores conta nas DUAS
    //raias: é o peso que cada uma tem de verdade, e é por ele que elas são ordenadas.
    const counts = new Map<number, number>()
    instances.forEach(instance => {
        valuesOf(instance).forEach(id => counts.set(id, (counts.get(id) || 0) + 1))
    })

    const options: LaneOption[] = Array.from(related.entries())
        .map(([id, value]) => ({id, name: value?._identifier || String(id), color: instanceColor(id)}))

    //Ordenados por QUANTIDADE, do maior para o menor. Situação e seleção têm uma ordem que o
    //usuário desenhou — a do fluxo, a do domínio —, e mexer nela seria desfazer uma decisão dele.
    //Relacionamento não tem ordem nenhuma: a de descoberta é acaso. Então a ordem útil é a do
    //peso, e é ela que põe na frente as raias e os blocos onde o trabalho está — e o candidato
    //sem item nenhum, que pesa zero, cai naturalmente para o fim.
    //Empate fica na ordem de descoberta, que o `sort` estável da linguagem preserva — sem isso
    //duas iguais trocariam de lugar a cada carga.
    //
    //O grupo vazio fica FORA da ordenação, sempre no fim: ele não é um valor entre os valores, é
    //a ausência deles, e por isso tem lugar fixo em qualquer dimensão — na seleção ele já vinha
    //depois das opções do domínio.
    const values: LaneOption[] = [
        ...options.sort((a, b) => (counts.get(b.id) || 0) - (counts.get(a.id) || 0)),
        ...noValueGroup(property, !!counts.get(NO_VALUE_ID), noValueName)
    ]
    return {
        key: property.alias,
        name: property.name,
        kind: 'rel',
        property,
        values,
        //Padrão: os candidatos que TÊM item. Eles fizeram a lista crescer para dar destino ao
        //arrasto, e não para encher o quadro de raia vazia — quem quiser vê-las marca no menu.
        //
        //O grupo vazio entra SEMPRE, tendo item ou não, pela mesma razão que lhe dá lugar fixo na
        //ordem: ele não é um candidato entre os candidatos, é a ausência deles. É por ele que se
        //TIRA o valor de um card arrastando, e escondê-lo justamente no quadro em que ninguém
        //está sem valor tiraria a única forma de esvaziar um campo. E custa uma raia, uma só —
        //não é dele que vem o problema de quantidade.
        defaultValues: values
            .filter(value => value.id === NO_VALUE_ID || !!counts.get(value.id))
            .map(value => value.id),
        valuesOf,
        ...shapeOf(property, ideas),
        relatedOf: (id: number) => related.get(id)
    }
}

/**
 * O grupo "Não definido", quando ele precisa existir.
 *
 * Existe quando a propriedade não é obrigatória — e também quando é, mas há item sem valor:
 * dado de antes de a obrigatoriedade ter sido posta. Sem essa segunda metade o item não teria
 * grupo nenhum e simplesmente desapareceria do quadro, que é pior do que mostrar um grupo que
 * em teoria não devia existir.
 */
function noValueGroup(property: IdeaProperty, missing: boolean, name: string): LaneOption[] {
    //Sempre cinza, em qualquer tipo de dimensão: é o que o cinza quer dizer no app, e é por isso
    //que ele saiu da paleta que se escolhe e da que se atribui.
    return (!property.required || missing) ? [{id: NO_VALUE_ID, name, color: NO_VALUE_COLOR}] : []
}

/** Há item sem valor nesta dimensão? É a segunda metade da pergunta do `noValueGroup`. */
function hasMissing(instances: Instance[], valuesOf: (instance: Instance) => number[]): boolean {
    return instances.some(instance => valuesOf(instance).includes(NO_VALUE_ID))
}

/**
 * O mesmo desembrulho do `propertyDisplay`, com a tradução que só o quadro precisa: ausência
 * vira o grupo "Não definido", que aqui é um valor como outro qualquer.
 */
function relatedId(value: any): number {
    return idOfRelated(value) ?? NO_VALUE_ID
}

/**
 * Todas as dimensões que esta coleção oferece, na ordem em que aparecem para escolher: a
 * situação primeiro, quando existe, depois as propriedades na ordem de declaração.
 *
 * A situação vem primeiro porque é o padrão — o Kanban nasceu sendo ela, e um quadro com uma
 * dimensão só continua idêntico ao de antes das dimensões existirem.
 */
export function dimensionsOf(
    idea: Idea, ideas: Idea[], workflow: EnhancedWorkflow | undefined, lookup: ValueLookup,
    instances: Instance[], names: DimensionNames, candidates: Candidates = {}
): Dimension[] {
    const dimensions: Dimension[] = []
    if (workflow) dimensions.push(stateDimension(workflow, names.state))
    ;(idea.properties || [])
        .filter(property => isDimensionProperty(property, idea.alias, ideas))
        .forEach(property => dimensions.push(property.relationship
            ? relationshipDimension(property, instances, names.noValue,
                candidates[property.relationship.idea] || [], ideas)
            : property.type === 'user'
                ? userDimension(property, lookup.people || [], instances, names.noValue)
                : choiceDimension(property, lookup.domains || [], instances, names.noValue)))
    return dimensions
}

/**
 * Os itens de cada coleção apontada, por alias — a forma exata do `relDomains` que o servidor
 * devolve com a opção `rel-domains`.
 *
 * Mapa, e não lista: duas propriedades podem apontar para a MESMA coleção (líder técnico e líder
 * de negócios, ambos Pessoa), e devem dividir uma leitura só.
 */
export type Candidates = { [ideaAlias: string]: any[] }

/**
 * As chaves que o recorte do quadro já prende a um valor só.
 *
 * Agrupar por uma delas daria uma raia com tudo dentro e um menu com uma opção que não escolhe
 * nada. É o caso do Kanban de um relacionamento: as tarefas DESTE projeto são filtradas por
 * `{projeto: 42}`, e "projeto" seria uma dimensão de um valor.
 *
 * A pergunta é feita ao FILTRO, e não ao relacionamento de volta, porque é o filtro que sabe o
 * que está preso — e assim a regra vale para qualquer quadro recortado que venha depois, sem
 * cada tela ter de contá-la.
 *
 * Só valor escalar conta: `{_instance: {$in: [...]}}` — o recorte do múltiplo direto — não
 * prende dimensão nenhuma, e um operador com vários valores ainda teria o que comparar.
 */
export function fixedByFilter(filter: Plain | null | undefined): string[] {
    if (!filter) return []
    return Object.keys(filter).filter(key => {
        const value = filter[key]
        return value !== null && typeof value !== 'object'
    })
}

/**
 * Os dois rótulos que as dimensões não têm como saber: o nome da situação e o do grupo vazio.
 *
 * Chegam de fora porque são traduzidos, e um utilitário puro não fala i18n — é o mesmo arranjo
 * do `create-label` da faixa, que também desce pronto de quem tem o idioma em mão.
 */
export type DimensionNames = { state: string, noValue: string }

/**
 * O que precisa mudar para o item passar a morar nesta combinação de valores.
 *
 * **Só o que DIFERE entra.** Mandar a dimensão que não mudou gravaria o mesmo valor — e no
 * caso da situação carimbaria um `_stateChangedAt` novo à toa, o que faria o item ressuscitar
 * numa faixa final cujo prazo já o tinha escondido. É o tipo de defeito que só aparece dias
 * depois, e num item que ninguém tocou.
 *
 * O grupo "Não definido" vira `null`, que é como o servidor entende "apague este campo" — é o
 * que acontece ao arrastar um card para ele.
 *
 * **Numa dimensão múltipla o item já mora em várias raias**, e aí o gesto tem duas leituras: sair
 * da raia de ORIGEM e entrar na de destino (`move`), ou passar a estar nas duas (`add`). Por isso
 * entram a origem e a ação — sem a origem não há o que substituir.
 *
 * Nela o "Não definido" se lê sozinho: ir para lá é **tirar o vínculo da origem**, o mesmo que o
 * relacionamento simples já faz ao ser esvaziado. Ficando sem nenhum, a lista vai vazia, que é
 * como "sem vínculo" se escreve numa lista — nunca `null` dentro dela.
 */
export type MoveAction = 'move' | 'add'

export function changesFor(
    instance: Instance, target: Coordinate, dimensions: Dimension[],
    origin: Coordinate = {}, action: MoveAction = 'move'
): Plain {
    const changes: Plain = {}
    dimensions.forEach(dimension => {
        const wanted = target[dimension.key]
        if (wanted === undefined) return
        const current = dimension.valuesOf(instance).filter(id => id !== NO_VALUE_ID)
        if (!dimension.multiple) {
            if (current[0] === wanted || (!current.length && wanted === NO_VALUE_ID)) return
            changes[dimension.key] = valueFor(dimension, [wanted])
            return
        }
        const from = origin[dimension.key]
        const without = current.filter(id => id !== from)
        const next = wanted === NO_VALUE_ID ? without
            : action === 'add' ? (current.includes(wanted) ? current : [...current, wanted])
                : without.includes(wanted) ? without : [...without, wanted]
        if (sameSet(current, next)) return
        changes[dimension.key] = valueFor(dimension, next)
    })
    return changes
}

/**
 * O valor que vai ao servidor para este conjunto de ids, na FORMA que o campo pede.
 *
 * Não é peça nova: é a regra que o `changesFor` já aplicava em dois ramos, e com os dois lendo
 * daqui ela passa a ter um dono só.
 *
 * **Quantos valores cabem e como o campo viaja são perguntas diferentes** — um espelho de campo
 * exclusivo comporta UM item e ainda assim é gravado como array, porque o valor dele mora nos
 * documentos do outro lado. Confundir as duas mandava um id solto onde o servidor espera a
 * lista, e isso já voltou 500 uma vez. Ver `multiple` e `list` no tipo `Dimension`.
 *
 * O `NO_VALUE_ID` sai aqui: ele é o grupo "sem valor", um id de mentira que serve ao
 * agrupamento e nunca ao documento.
 */
function valueFor(dimension: Dimension, ids: number[]): any {
    const wanted = ids.filter(id => id !== NO_VALUE_ID)
    if (dimension.list) return wanted
    return wanted.length ? wanted[0] : null
}

/**
 * O conjunto escolhido À MÃO, numa dimensão só: o que o modal de troca rápida grava.
 *
 * Diferente do `changesFor`, que parte de um GESTO — um destino, uma origem e a dúvida entre
 * mover e acrescentar. Aqui não há dúvida nenhuma a resolver: marcando as caixas o usuário
 * declarou o conjunto final, e desmarcar é como se tira um valor. É por isso que este caminho
 * não pergunta "mover ou acrescentar", e o arrasto pergunta.
 *
 * Devolve `{}` quando o conjunto é o mesmo, para não gastar uma requisição dizendo nada — pela
 * mesma conta de conjunto que o `changesFor` usa, que ignora a ordem.
 */
export function changesForValues(instance: Instance, dimension: Dimension, ids: number[]): Plain {
    const current = dimension.valuesOf(instance).filter(id => id !== NO_VALUE_ID)
    const wanted = ids.filter(id => id !== NO_VALUE_ID)
    if (sameSet(current, wanted)) return {}
    return {[dimension.key]: valueFor(dimension, wanted)}
}

/** Os dois conjuntos têm os mesmos ids? A ordem de um relacionamento múltiplo não é significativa. */
function sameSet(a: number[], b: number[]): boolean {
    return a.length === b.length && a.every(id => b.includes(id))
}

/**
 * Este gesto precisa perguntar "mover ou acrescentar"?
 *
 * Só quando ele de fato altera uma dimensão MÚLTIPLA — a forma da dimensão sozinha não basta.
 * Cruzar um bloco mantendo a raia múltipla não muda nada nela, e perguntar ali seria pedir uma
 * decisão que não existe.
 *
 * E não pergunta quando o card JÁ ESTÁ no destino: ali "acrescentar" não faria nada, então sobra
 * uma leitura só — sair da origem. Nem no "Não definido", que também se lê de um jeito só.
 *
 * **Nem saindo do "Não definido"**: sem vínculo nenhum não há de onde sair, então mover e
 * acrescentar gravam a mesma coisa — a lista com o destino e mais nada. Perguntar ali seria
 * oferecer duas saídas para o mesmo lugar.
 */
export function asksHowToMove(
    instance: Instance, target: Coordinate, dimensions: Dimension[]
): boolean {
    return dimensions.some(dimension => {
        if (!dimension.multiple) return false
        const wanted = target[dimension.key]
        if (wanted === undefined || wanted === NO_VALUE_ID) return false
        const current = dimension.valuesOf(instance).filter(id => id !== NO_VALUE_ID)
        if (!current.length) return false
        return !current.includes(wanted)
    })
}

/**
 * Os valores com que um item nasce, para nascer nesta combinação: o que o "+" da faixa semeia
 * no formulário de criar.
 *
 * O grupo "Não definido" não semeia nada — nascer sem valor é justamente não receber o campo, e
 * mandar o sentinela gravaria o id 0, que não é opção de nada.
 *
 * **Campo de lista semeia LISTA**: um id solto ali é o mesmo tipo errado que o servidor recusaria,
 * e que o formulário não saberia desenhar.
 */
export function seedValues(target: Coordinate, dimensions: Dimension[] = []): Plain {
    const values: Plain = {}
    Object.keys(target)
        .filter(key => target[key] !== NO_VALUE_ID)
        .forEach(key => {
            //Pela FORMA do campo, e não pela cardinalidade: o que decide se vai lista é como o
            //campo é gravado, não quantos valores ele comporta.
            const list = dimensions.some(dimension => dimension.key === key && dimension.list)
            values[key] = list ? [target[key]] : target[key]
        })
    return values
}

/**
 * Espelha no objeto em memória o que o servidor acabou de gravar, para o quadro não precisar
 * recarregar a cada arrasto.
 *
 * Relacionamento precisa dos DOIS lados: o campo cru e o `_rels`. O `propertyValue` lê o
 * `_rels` primeiro, então mexer só no campo deixaria o card mostrando o valor antigo — com o
 * nome antigo e a bolinha antiga.
 */
export function applyChangesLocally(instance: Instance, changes: Plain, dimensions: Dimension[]) {
    dimensions.forEach(dimension => {
        if (!(dimension.key in changes)) return
        //O `changes` é Plain por contrato do serviço, mas o que uma dimensão põe ali é sempre
        //id, null ou LISTA de ids — foi este módulo que o montou, no changesFor.
        const written = changes[dimension.key] as number | number[] | null
        instance[dimension.key] = written === null ? undefined : written
        if (dimension.kind === 'state') {
            //O carimbo que o endpoint de situação acabou de gravar: sem ele um item que já
            //passou por uma situação final carrega data velha e some no reagrupamento.
            instance._stateChangedAt = new Date().toISOString()
            return
        }
        if (dimension.kind !== 'rel') return
        //Objeto novo, e não mutação por chave: o `_rels` chega do servidor e não passa pelo
        //data(), então trocar a referência é o que não depende de ele ser reativo por proxy.
        //
        //Numa dimensão múltipla o `_rels` é LISTA, e tem de ser remontado item a item: gravar
        //ali o objeto de um só deixaria o card mostrando um vínculo onde há vários.
        const value = written === null ? []
            : Array.isArray(written) ? written.map(id => dimension.relatedOf!(id)).filter(it => !!it)
                : dimension.relatedOf!(written)
        instance._rels = {...(instance._rels || {}), [dimension.key]: value}
    })
}

/**
 * Onde um bloco está nas dimensões: valor por chave de dimensão.
 *
 * Sempre um id, nunca `undefined`: item sem valor mora no grupo `NO_VALUE_ID`, e é por isso que
 * nem a coordenada, nem o rótulo, nem a preferência guardada precisam de um caso do vazio.
 */
export type Coordinate = { [dimensionKey: string]: number }

/**
 * Este item mora nesta combinação de valores? Em cada dimensão que a coordenada diz, o valor dela
 * está entre os do item — `includes`, e não igualdade: numa dimensão múltipla o item mora em vários.
 *
 * É a regra do agrupamento, num lugar só: o bloco a usa para separar os seus itens, e o "+" da raia
 * na ficha, para oferecer a vincular só os itens que já moram ali ("se a raia é Samba, só aparecem
 * sambas" — pedido do usuário). Uma consulta própria ao servidor teria de repetir os casos que o
 * `valuesOf` já resolve, como o item sem situação gravada contando na inicial.
 */
/**
 * O que o "+" de uma raia entrega a quem hospeda o quadro (ver o `addInLane` do KanbanBoard): os
 * valores com que o item novo nasce ali, e quem mora nela.
 */
export type LaneAdd = {
    seed: Plain
    // eslint-disable-next-line no-unused-vars
    accepts: (instance: Instance) => boolean
    /**
     * Os valores da raia — e, com blocos, os do bloco —, para a janela de vincular mostrar a
     * restrição com o chip da raia (escolha do usuário entre chip, dica e subtítulo).
     */
    values: CoordinateValue[]
}

/**
 * Um valor de uma combinação, nas formas em que a tela o desenha: a opção colorida, que serve a toda
 * dimensão — o grupo "Não definido" é uma delas —, e o item apontado, quando a dimensão é um
 * relacionamento (`undefined` no "Não definido"). É o que o `LaneValue` desenha.
 */
export type CoordinateValue = {
    dimension: Dimension
    option?: LaneOption
    related?: any
}

export function coordinateValue(dimension: Dimension, id: number): CoordinateValue {
    return {dimension, option: dimension.values.find(value => value.id === id), related: dimension.relatedOf?.(id)}
}

/**
 * Os valores de uma combinação, na ordem das dimensões. Um dono para o rótulo de cada bloco, as pontas
 * do "mover ou acrescentar" e a restrição do "+" da raia — eram três montagens iguais.
 */
export function coordinateValues(coordinate: Coordinate, dimensions: Dimension[]): CoordinateValue[] {
    return dimensions
        .filter(dimension => coordinate[dimension.key] !== undefined)
        .map(dimension => coordinateValue(dimension, coordinate[dimension.key]))
}

export function inCoordinate(instance: Instance, coordinate: Coordinate, dimensions: Dimension[]): boolean {
    return dimensions
        .filter(dimension => coordinate[dimension.key] !== undefined)
        .every(dimension => dimension.valuesOf(instance).includes(coordinate[dimension.key]))
}

/**
 * A dimensão que não agrupa nada.
 *
 * Existe para quem precisa responder "qual é a primeira dimensão?" num instante em que não há
 * nenhuma — entre o quadro descobrir que a coleção não tem por onde agrupar e o hospedeiro
 * navegar para fora. Sem ela, esse intervalo é um `undefined` desreferenciado.
 */
export const EMPTY_DIMENSION: Dimension = {
    key: '', name: '', kind: 'choice', values: [], valuesOf: () => [NO_VALUE_ID]
}

/** O id de um valor guardado; o do grupo "Não definido" quando não há valor algum. */
/**
 * Em que grupos este valor põe o item. Nunca vazio: sem valor nenhum, `[NO_VALUE_ID]`.
 *
 * **Uma só para as duas espécies de dimensão.** A pergunta é a mesma — desembrulhe o campo,
 * traduza cada valor para id, descarte os vazios —, e a diferença entre um relacionamento e uma
 * seleção está só em DE ONDE se lê o campo, que é decisão de quem chama.
 *
 * Isto aposenta o `idOf`, que fazia o mesmo cálculo para a seleção com outro nome: o
 * `relatedId` já trata as duas formas de valor, o objeto do `_rels` e o id solto, porque o
 * `relatedId` do `propertyDisplay` que ele embrulha olha o `_instance` só quando há objeto.
 */
function groupsOf(value: any): number[] {
    const ids = asArray(value).map(value => relatedId(value)).filter(id => id !== NO_VALUE_ID)
    return ids.length ? ids : [NO_VALUE_ID]
}

/**
 * As duas perguntas que o arrasto faz sobre a dimensão, respondidas no mesmo lugar para o
 * relacionamento e para a seleção — ver `multiple` e `list` no tipo `Dimension`.
 *
 * `ideas` tem padrão porque um ATRIBUTO não tem outro lado a resolver: só o espelho precisa
 * olhar a propriedade de lá. É o que evita pendurar um parâmetro no `choiceDimension` para
 * satisfazer uma assinatura que ele não usa.
 */
function shapeOf(property: IdeaProperty, ideas: Idea[] = []): {multiple: boolean; list: boolean} {
    return {
        multiple: !!effectiveCardinality(property, resolverFor(ideas)).multiple,
        list: travelsAsList(property)
    }
}
